import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import type { Update } from "./use-product-updates";

const STORAGE_KEY = "lyder-talk-seen-updates";
const CUSTOM_EVENT_NAME = "seen-updates-changed";

/**
 * Hook to track which product updates the user has seen.
 * Uses localStorage to persist seen status across sessions.
 * Synchronizes state across all hook instances using custom events.
 */
export function useSeenUpdates() {
    const [seenUpdateIds, setSeenUpdateIds] = useState<Set<string>>(new Set());
    const isInternalUpdateRef = useRef(false);
    const hasLoadedRef = useRef(false);

    // Load seen updates from localStorage on mount
    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored) as string[];
                setSeenUpdateIds(new Set(parsed));
            }
            hasLoadedRef.current = true;
        } catch (error) {
            console.error("Error loading seen updates from localStorage:", error);
            hasLoadedRef.current = true;
        }
    }, []);

    // Listen for custom events from other hook instances
    useEffect(() => {
        const handleStorageChange = (event: Event) => {
            // Ignore events triggered by this instance
            if (isInternalUpdateRef.current) {
                return;
            }
            
            // Type guard for CustomEvent
            if (!(event instanceof CustomEvent) || !Array.isArray(event.detail)) {
                return;
            }
            
            // Only update if the new state is different from current state
            const newIds = new Set(event.detail as string[]);
            setSeenUpdateIds((prev) => {
                // Check if sets are different
                if (prev.size !== newIds.size) {
                    return newIds;
                }
                for (const id of prev) {
                    if (!newIds.has(id)) {
                        return newIds;
                    }
                }
                // Sets are identical, no update needed
                return prev;
            });
        };

        // Add event listener for custom event
        window.addEventListener(CUSTOM_EVENT_NAME, handleStorageChange);

        return () => {
            window.removeEventListener(CUSTOM_EVENT_NAME, handleStorageChange);
        };
    }, []);

    // Save seen updates to localStorage whenever it changes and notify other instances
    useEffect(() => {
        // Skip if we haven't loaded from localStorage yet (initial mount)
        if (!hasLoadedRef.current) {
            return;
        }

        try {
            const idsArray = Array.from(seenUpdateIds);
            
            if (idsArray.length > 0) {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(idsArray));
            } else {
                localStorage.removeItem(STORAGE_KEY);
            }
            
            // Dispatch custom event to notify other hook instances
            isInternalUpdateRef.current = true;
            window.dispatchEvent(
                new CustomEvent(CUSTOM_EVENT_NAME, { detail: idsArray })
            );
            // Reset flag after event has been dispatched
            // Use requestAnimationFrame to ensure event propagates first
            requestAnimationFrame(() => {
                isInternalUpdateRef.current = false;
            });
        } catch (error) {
            console.error("Error saving seen updates to localStorage:", error);
            isInternalUpdateRef.current = false;
        }
    }, [seenUpdateIds]);

    /**
     * Mark an update as seen
     */
    const markAsSeen = useCallback((updateId: string) => {
        setSeenUpdateIds((prev) => {
            const next = new Set(prev);
            next.add(updateId);
            return next;
        });
    }, []);

    /**
     * Mark multiple updates as seen
     */
    const markMultipleAsSeen = useCallback((updateIds: string[]) => {
        setSeenUpdateIds((prev) => {
            const next = new Set(prev);
            updateIds.forEach((id) => next.add(id));
            return next;
        });
    }, []);

    /**
     * Check if an update has been seen
     */
    const hasSeen = useCallback(
        (updateId: string) => {
            return seenUpdateIds.has(updateId);
        },
        [seenUpdateIds]
    );

    /**
     * Get the count of unseen updates
     */
    const getUnseenCount = useCallback(
        (updates: Update[]) => {
            return updates.filter((update) => !seenUpdateIds.has(update.id)).length;
        },
        [seenUpdateIds]
    );

    /**
     * Get unseen updates
     */
    const getUnseenUpdates = useCallback(
        (updates: Update[]) => {
            return updates.filter((update) => !seenUpdateIds.has(update.id));
        },
        [seenUpdateIds]
    );

    /**
     * Clear all seen updates (useful for testing or reset)
     */
    const clearSeenUpdates = useCallback(() => {
        setSeenUpdateIds(new Set());
        localStorage.removeItem(STORAGE_KEY);
    }, []);

    return {
        seenUpdateIds: useMemo(() => Array.from(seenUpdateIds), [seenUpdateIds]),
        markAsSeen,
        markMultipleAsSeen,
        hasSeen,
        getUnseenCount,
        getUnseenUpdates,
        clearSeenUpdates,
    };
}
